/**
 * The browser's history as the app's navigation: pushing a screen or
 * presenting a sheet adds a history entry, and the browser's back (a
 * phone's edge swipe, the toolbar button, Alt+Left) pops or dismisses it,
 * as the bar's own back button would. Forward cannot re-push a screen, so a
 * forward step lands back where the app is.
 *
 * Depth is read from the DOM after every commit: the presented sheets, plus
 * the pushed screens of every visible stack (the selected tab's, both
 * columns of a split view, the top sheet's). Pops go through the same event
 * the back button and the scrim send, so the app's own state stays the
 * source of truth; the page's URL is never touched, and an app that maps
 * screens to paths should set them with `replaceState` on the entry the
 * renderer made. When the app pops on its own, the renderer's `history.go`
 * back is asynchronous and the app's `replaceState` for the screen it shows
 * now runs before the move lands, on the entry being left; the renderer
 * applies that URL again to the entry it arrives at, so the address bar
 * follows the bar's back button as it follows the browser's.
 *
 * A two-column split view pushes nothing when its sidebar's selection
 * changes, so that is an entry of its own: choosing another sidebar row
 * (one the selection moves to, not the first choice) adds one, and back or
 * forward taps the row the entry recorded, through the same `tap` a finger
 * sends. In a compact width the selection pushes the detail, a level like
 * any other.
 *
 * While enabled, the renderer's own edge swipe to pop is off for touch and
 * pen: the browser's edge swipe (Safari's, Android's back gesture) is the
 * swipe back, and it lands here as a `popstate`. A mouse drag from the edge
 * still pops through the renderer, since no browser turns one into history.
 *
 * Every `popstate` the browser makes also starts the renderer's repaint
 * keep-alive (`Renderer.keepRepainting`): Safari's swipe shows a snapshot of
 * the entry and, once its animation ends, hides the live page until a new
 * frame is committed, so a pop that finished animating by then would stay
 * under the snapshot for Safari's 3 s watchdog, taking the touches unseen.
 * A push is made at the renderer's `sb:precommit`, before the pushed screen
 * is laid out (see `sync`), and this module never forces a layout.
 */
import type { Renderer } from './renderer';

interface HistoryState {
  /** Our marker; entries without it belong to the page or the app. */
  sbHistory: true;
  /** How many levels (screens and sheets) are above the base in this entry. */
  sbDepth: number;
  /** The selected sidebar rows of the visible two-column split views (`sbId`s, comma-separated; absent or empty for none). */
  sbSelection?: string;
}

/** What the renderer's elements need to say for this to read them (`sbId`, `sbDepth` datasets). */
const stateOf = (value: unknown): HistoryState | null => (typeof value === 'object' && value !== null && (value as HistoryState).sbHistory === true ? (value as HistoryState) : null);

function shown(win: Window, el: HTMLElement): boolean {
  if (!el.isConnected) return false;
  // Style only, never layout: a layout forced here before a push would hand
  // Safari the pushed screen's size for the entry it covers (see `sync`).
  for (let node: HTMLElement | null = el; node; node = node.parentElement) {
    if (node.hidden || win.getComputedStyle(node).display === 'none') return false;
  }
  return win.getComputedStyle(el).visibility !== 'hidden';
}

/**
 * A back the browser makes after a touch this close to a side of the page is
 * its edge swipe (Safari's, Android's back gesture), which has animated the
 * pop already: the renderer then does not slide the screen out again. The
 * browser's back button sends no touch to the page, so its pop slides.
 */
const SWIPE_EDGE_PX = 44;
/** How long after such a touch a back still counts as the swipe it started. */
const SWIPE_TOUCH_MS = 2000;

/** When `sync` runs: the renderer's `sb:precommit` (the pass's ops applied, not yet laid out), its `sb:commit`, or `enable`. */
export type SyncStage = 'precommit' | 'commit' | 'enabling';

/** How long a back the browser made waits for the app to pop a level before what it shows is recorded as it is. */
const DISMISS_WAIT_MS = 1000;

export class BrowserHistory {
  private readonly win: Window;
  /**
   * A back the browser made, while the app pops to it: the depth the entry
   * names, the depth the latest dismissal started from, and until when the
   * app's pop is waited for. A pop can take more than one commit (a compact
   * split view clears its sidebar selection first, and the detail goes on
   * the next), so a commit that still shows the level is not a new one.
   */
  private dismissing: { target: number; from: number; until: number; instant: boolean } | null = null;
  /** When a touch last began at a side of the page (`SWIPE_EDGE_PX`): the start of the browser's edge swipe. */
  private lastEdgeTouch = Number.NEGATIVE_INFINITY;
  /** `popstate` events to ignore: the ones our own `history.go` causes. */
  private ignorePops = 0;
  /**
   * The depth our own `history.go` is travelling to while its `popstate` is
   * still on its way (`go` is asynchronous): commits meanwhile compare
   * against it, so a second commit does not go back a second time.
   */
  private movingTo: number | null = null;
  private movingTimer: ReturnType<typeof setTimeout> | null = null;
  /** A `popstate` asked for the entry's sidebar selection: the change it brings is recorded, not pushed. */
  private restoringSelection = false;
  private enabled = false;
  /** The page's own `replaceState`, while ours stands in for it. */
  private pageReplaceState: History['replaceState'] | null = null;
  private pageReplaceStateWasOwn = false;
  /** A URL the page set while our own `history.go` was on its way: meant for the entry it lands on. */
  private landingUrl: string | URL | null = null;
  /** A level appeared while our own `history.go` was on its way: its entry is pushed once the move lands. */
  private syncOnArrival = false;

  constructor(
    private readonly renderer: Renderer,
    private readonly root: HTMLElement,
    win: Window = window,
  ) {
    this.win = win;
  }

  /** Marks the current entry as the base and starts mirroring pushes and pops. */
  enable(): void {
    if (this.enabled) return;
    this.enabled = true;
    const current = stateOf(this.win.history.state);
    if (!current) this.win.history.replaceState({ ...(this.win.history.state ?? {}), ...this.entry(0, this.selection()) }, '');
    this.watchReplaceState();
    this.renderer.interaction.nativeBackGesture = true;
    this.root.addEventListener('sb:precommit', this.onPreCommit);
    this.root.addEventListener('sb:commit', this.onCommit);
    this.win.addEventListener('popstate', this.onPopState);
    this.win.addEventListener('touchstart', this.onTouchStart, { capture: true, passive: true });
    // Enabled before the first commit (a mount, or a remount such as React's
    // StrictMode makes in development) the screen is not drawn yet: fewer
    // levels than the entry records is not the app popping, so only the
    // levels already shown are pushed here and the first commit settles the rest.
    this.sync('enabling');
  }

  dispose(): void {
    if (!this.enabled) return;
    this.enabled = false;
    this.renderer.interaction.nativeBackGesture = false;
    if (this.movingTimer) clearTimeout(this.movingTimer);
    this.movingTo = null;
    this.landingUrl = null;
    this.syncOnArrival = false;
    this.unwatchReplaceState();
    this.root.removeEventListener('sb:precommit', this.onPreCommit);
    this.root.removeEventListener('sb:commit', this.onCommit);
    this.win.removeEventListener('popstate', this.onPopState);
    this.win.removeEventListener('touchstart', this.onTouchStart, { capture: true });
  }

  /**
   * Stands in for the page's `replaceState`: it still runs at once, and a
   * URL given while our own `go` is on its way is remembered for the entry
   * the move lands on.
   */
  private watchReplaceState(): void {
    const history = this.win.history;
    const original = history.replaceState;
    this.pageReplaceState = original;
    this.pageReplaceStateWasOwn = Object.prototype.hasOwnProperty.call(history, 'replaceState');
    history.replaceState = (data: unknown, unused: string, url?: string | URL | null): void => {
      if (this.movingTo !== null && url !== undefined && url !== null) this.landingUrl = url;
      original.call(history, data, unused, url);
    };
  }

  private unwatchReplaceState(): void {
    const original = this.pageReplaceState;
    if (!original) return;
    const history = this.win.history;
    // A browser's is the prototype's: removing ours uncovers it. A stand-in's own property is put back.
    if (this.pageReplaceStateWasOwn) history.replaceState = original;
    else delete (history as Partial<History>).replaceState;
    this.pageReplaceState = null;
  }

  /** `replaceState` as the page had it, for our own entries. */
  private replaceEntry(data: unknown, url?: string | URL | null): void {
    (this.pageReplaceState ?? this.win.history.replaceState).call(this.win.history, data, '', url);
  }

  /** The levels above the base the app shows now: sheets, plus pushed screens in each visible stack of the top layer. */
  depth(): number {
    const sheets = this.sheets();
    const top = sheets.at(-1) ?? this.root;
    let depth = sheets.length;
    for (const stack of this.stacks(top)) depth += this.topDepth(stack);
    return depth;
  }

  /** The selected sidebar rows of the visible two-column split views, as an entry records them. */
  selection(): string {
    const ids: string[] = [];
    for (const split of this.root.querySelectorAll<HTMLElement>('.sb-navsplit')) {
      if (!shown(this.win, split)) continue;
      // The sidebar column is the first stack; its selectable rows carry the selection.
      const sidebar = split.querySelector<HTMLElement>('.sb-navstack');
      if (!sidebar) continue;
      for (const row of sidebar.querySelectorAll<HTMLElement>('.sb-listrow[data-sb-selectable][data-sb-selected]')) {
        const id = row.dataset['sbId'];
        if (id !== undefined) ids.push(id);
      }
    }
    return ids.join(',');
  }

  /** Taps the sidebar rows an entry recorded as selected that are not selected now; false when none can be. */
  private selectRows(recorded: string): boolean {
    const current = new Set(this.selection().split(',').filter(Boolean));
    let tapped = false;
    for (const id of recorded.split(',').filter(Boolean)) {
      if (current.has(id)) continue;
      const row = this.root.querySelector<HTMLElement>(`.sb-listrow[data-sb-selectable][data-sb-id="${id}"]`);
      if (!row || !shown(this.win, row)) continue;
      this.renderer.tap(Number(id));
      tapped = true;
    }
    return tapped;
  }

  private sheets(): HTMLElement[] {
    return [...this.root.querySelectorAll<HTMLElement>(':scope > .sb-sheet')].filter((sheet) => shown(this.win, sheet));
  }

  /** The visible navigation stacks within `container`, not those inside a deeper sheet. */
  private stacks(container: HTMLElement): HTMLElement[] {
    return [...container.querySelectorAll<HTMLElement>('.sb-navstack')].filter((stack) => shown(this.win, stack) && stack.closest('.sb-sheet') === (container.classList.contains('sb-sheet') ? container : null));
  }

  private topScreen(stack: HTMLElement): HTMLElement | null {
    const screens = stack.querySelectorAll<HTMLElement>(':scope > .sb-slot > .sb-navscreen');
    return screens.length ? screens[screens.length - 1]! : null;
  }

  private topDepth(stack: HTMLElement): number {
    const top = this.topScreen(stack);
    return top ? Number(top.dataset['sbDepth'] ?? '0') || 0 : 0;
  }

  /** Dismisses the level above `depth` for the back in progress, and waits for the app's pop. */
  private dismissFrom(depth: number): void {
    const dismissing = this.dismissing;
    if (!dismissing) return;
    dismissing.from = depth;
    dismissing.until = performance.now() + DISMISS_WAIT_MS;
    if (!this.dismissOne(dismissing.instant)) this.dismissing = null;
  }

  /**
   * Asks the app to dismiss one level: the top sheet when nothing is pushed
   * inside it, else the deepest visible stack's top screen. `instant`: the
   * browser has animated the pop (its swipe), so the renderer does not.
   */
  private dismissOne(instant: boolean): boolean {
    const sheets = this.sheets();
    const top = sheets.at(-1) ?? this.root;
    const stacks = this.stacks(top)
      .map((stack) => ({ stack, depth: this.topDepth(stack) }))
      .filter((s) => s.depth > 0)
      .sort((a, b) => b.depth - a.depth);
    const deepest = stacks[0];
    if (deepest) {
      const screen = this.topScreen(deepest.stack);
      const id = Number(screen?.dataset['sbId']);
      if (!Number.isInteger(id)) return false;
      if (instant) this.renderer.popWithoutAnimation(id);
      this.renderer.tap(id); // what the back button sends
      return true;
    }
    const sheet = sheets.at(-1);
    const id = Number(sheet?.dataset['sbId']);
    if (!Number.isInteger(id)) return false;
    this.renderer.tap(id); // a dismiss request, as the scrim's tap
    return true;
  }

  private recorded(): number {
    return this.movingTo ?? stateOf(this.win.history.state)?.sbDepth ?? 0;
  }

  private recordedSelection(): string {
    return stateOf(this.win.history.state)?.sbSelection ?? '';
  }

  private entry(depth: number, selection: string): HistoryState {
    return selection ? { sbHistory: true, sbDepth: depth, sbSelection: selection } : { sbHistory: true, sbDepth: depth };
  }

  /** Our own `history.go`, remembered until its `popstate` lands (or a moment passes without one: a `go` past the ends fires none). */
  private go(delta: number, to: number): void {
    this.ignorePops += 1;
    this.movingTo = to;
    if (this.movingTimer) clearTimeout(this.movingTimer);
    this.movingTimer = setTimeout(() => {
      if (this.movingTo === null) return;
      this.movingTo = null;
      // No move (a go past the ends fires nothing): the URL already sits on the entry at hand.
      this.landingUrl = null;
      this.ignorePops = Math.max(0, this.ignorePops - 1);
      if (this.syncOnArrival) {
        this.syncOnArrival = false;
        this.sync();
      }
    }, 1000);
    this.win.history.go(delta);
  }

  private arrived(): void {
    this.movingTo = null;
    if (this.movingTimer) clearTimeout(this.movingTimer);
    this.movingTimer = null;
    // The page's URL for what it shows now, set on the entry we left: it belongs here.
    const url = this.landingUrl;
    this.landingUrl = null;
    if (url !== null) this.replaceEntry(this.win.history.state, url);
    // Levels that appeared during the move get their entries now.
    if (this.syncOnArrival) {
      this.syncOnArrival = false;
      this.sync();
    }
  }

  private readonly onPreCommit = (): void => this.sync('precommit');
  private readonly onCommit = (): void => this.sync('commit');

  /**
   * New levels get entries; levels the app dismissed itself take the history
   * back with them; a moved sidebar selection is an entry too.
   *
   * The entries are pushed at the `precommit`, before the renderer lays the
   * new screen out, and nothing else happens then: Safari sizes the entry
   * being left from the page's last layout at the push (its snapshot's
   * render tree size, more than half of which the page must have again
   * before the snapshot comes down after a swipe back to it), so a push
   * after that layout counted the pushed screen into the entry it covers,
   * and a root lighter than the screen over it stayed under the snapshot
   * until Safari's 3 s watchdog after every swipe back. The rest waits for
   * the `commit`, when the pass is drawn. `enabling`: nothing is drawn yet,
   * so no level is taken as dismissed.
   */
  sync(stage: SyncStage = 'commit'): void {
    if (!this.enabled) return;
    const dismissing = this.dismissing;
    if (stage === 'precommit' && dismissing) return;
    const depth = this.depth();
    const recorded = this.recorded();
    const selection = this.selection();
    if (dismissing) {
      // Dismissing for a `popstate`: the entry already names the target depth.
      if (depth > dismissing.target && performance.now() < dismissing.until) {
        // The latest dismissal landed: the next level. Otherwise the app has not popped yet.
        if (depth < dismissing.from) this.dismissFrom(depth);
        return;
      }
      // Arrived, or the app kept the level (it is then recorded as any other).
      this.dismissing = null;
    }
    if (depth > recorded) {
      // Under our own pending `go`, a push would land relative to wherever the browser is when it runs (and
      // Chrome moves back past it), leaving the app on an entry below what it shows: push once the move lands.
      if (this.movingTo !== null) {
        this.syncOnArrival = true;
        return;
      }
      for (let level = recorded + 1; level <= depth; level++) this.win.history.pushState(this.entry(level, selection), '');
    } else if (depth < recorded) {
      if (stage !== 'commit') return;
      // The app popped or dismissed on its own (back button, swipe, code): leave those entries.
      this.go(depth - recorded, depth);
    } else if (this.movingTo === null && selection !== this.recordedSelection()) {
      // The same levels, another sidebar selection. The first choice (from
      // none) and the one a `popstate` asked for belong to the entry at hand;
      // a move from one row to another is a new entry.
      const history = this.win.history;
      if (this.restoringSelection || !this.recordedSelection()) {
        const { sbSelection: _, ...rest } = (history.state ?? {}) as Partial<HistoryState>;
        this.replaceEntry({ ...rest, ...this.entry(depth, selection) });
      } else history.pushState(this.entry(depth, selection), '');
    }
    this.restoringSelection = false;
  }

  private readonly onTouchStart = (event: TouchEvent): void => {
    const touch = event.touches[0];
    if (!touch) return;
    if (touch.clientX <= SWIPE_EDGE_PX || touch.clientX >= this.win.innerWidth - SWIPE_EDGE_PX) this.lastEdgeTouch = performance.now();
  };

  private readonly onPopState = (event: PopStateEvent): void => {
    if (this.ignorePops > 0) {
      this.ignorePops -= 1;
      this.arrived();
      return;
    }
    const target = stateOf(event.state);
    if (!target) return; // an entry of the page's own (before the base, or the app's router): not ours
    // The browser moved on its own (a swipe, a button): keep painting past its transition.
    this.renderer.keepRepainting();
    const depth = this.depth();
    // A move the browser makes replaces any back still in progress.
    this.dismissing = null;
    if (target.sbDepth < depth) {
      // Back: dismiss one level now, the rest as each pop lands. After a
      // touch at a side of the page it is the browser's swipe, animated already.
      const instant = performance.now() - this.lastEdgeTouch < SWIPE_TOUCH_MS;
      this.dismissing = { target: target.sbDepth, from: depth, until: 0, instant };
      this.dismissFrom(depth);
    } else if (target.sbDepth > depth) {
      // Forward: a screen cannot be re-pushed; return to the entry that matches.
      this.go(depth - target.sbDepth, depth);
      return;
    }
    // The entry's sidebar selection, where it differs: tap the rows it names
    // (back and forward alike, a row can be chosen again). What the app
    // shows after that is recorded on this entry rather than pushed.
    const recorded = target.sbSelection ?? '';
    if (recorded !== this.selection()) {
      this.restoringSelection = true;
      this.selectRows(recorded);
    }
  };
}
